import { EventEmitter, Injectable, Output } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { User } from '../users/model/user.model';

const httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    rejectUnauthorized: false,
};

@Injectable()
export class ApiService {

    @Output() tokenExpired: EventEmitter<any> = new EventEmitter();

    private readonly poesslApiUrl = environment.poesslApiUrl;
    public readonly apiUrl = environment.apiUrl;
    private readonly apiKey = environment.apiKey;

    public readonly apiUrlLive = environment.apiUrlLive;
    public readonly apiUrlTest = environment.apiUrlTest;

    private readonly unsplashUrl = 'https://api.unsplash.com/';
    private readonly unsplashKey = 'b9363d48595e30c5239b6ba1f7f401729a1c9de4232c5bdb1567de1ecec93d05';

    constructor(private http: HttpClient) {

    }

    public login(email: string, password: string): Observable<any[]> {
        return this.http
            .post<any>(
                `${this.apiUrl}/auth/sign-in`,
                { email, password },
                httpOptions
            );
    }

    public requestPasswordReset(email: string): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/auth/request-password-reset/` + email,
                httpOptions
            );
    }

    public sendWelcomeMail(email: string): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/auth-secure/send-welcome-mail/` + email,
                httpOptions
            );
    }

    public resetPassword(email: string, token: string, password: string) {
        return this.http
            .post<any>(
                `${this.apiUrl}/auth/reset-password`,
                { email, token, password },
                httpOptions
            )
    }

    public refreshToken() {
        return this.http
            .get<any>(
                `${this.apiUrl}/auth/refresh-token`,
                httpOptions
            ).subscribe(data => {
                console.log(data);
            });
    }

    public getUsers(offset: number = 0, limit: number = 25) {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/users/${offset}/${limit}`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getUserDetails(user: User) {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/user/${user.id}`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getProjects() {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/projects/company`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getBackendRoles() {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/backend-roles`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public saveUser(user: User) {
        if (user.id) {
            return this.http
                .put<any>(
                    `${this.apiUrl}/private/user/${user.id}`,
                    user,
                    httpOptions
                )
                .pipe(
                    catchError(this.handleError)
                )
                .subscribe();
        } else {
            user.contact.email = user.email
            return this.http
                .post<any>(
                    `${this.apiUrl}/private/user`,
                    user,
                    httpOptions
                )
                .pipe(
                    catchError(this.handleError)
                )
                .subscribe();
        }

    }

    public getChatForCustomer(email: string, orderHash: string): Observable<any[]> {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/public/chat/customer`,
                { email, orderHash }, // dieses obj ist im node express der req.body als z.b. req.body.email usw.
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getOrdersForAdmin(from, to, excludeIds: any[] = []) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/orders`,
                // `https://api.dieshop.app:8082/private/orders`,
                {
                    from, to, excludeIds
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getContactData(): Observable<any[]> {
        return this.http.get<any[]>(
            `${this.apiUrl}/private/addresses/`
        );
    }

    private handleError = (error: HttpErrorResponse) => {
        if (error.status === 0) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error);
        } else if (error.status == 401 || error.status == 403) {
            console.error(`Server returned ${error.status}. Emitting tokenExpired event...`);
            this.tokenExpired.emit(null);
            return
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.

            console.error(
                `Backend returned code ${error.status}, body was: `, error.error);
        }

        // Return an observable with a user-facing error message.
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }

    addTimeLog(projectId, timeLogId, timeLog = null) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-log`,
                {
                    projectId, timeLogId, timeLog
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deleteTimeLog(timeLog) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-log/delete`,
                {
                    timeLog,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editTimeLog(timeLog) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-log/edit`,
                {
                    timeLog,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editLock(item) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/edit`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    moveLock(item) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/move`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }


    deleteLock(item) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/delete`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    addLock(item) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/add`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getTimeLogs(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-logs`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLocationControllerPing(controller) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/ping`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLocationController(controller) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    addLocationController(controller: any) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/add`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editLocationController(controller: any) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/edit`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLocationControllers() {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .get<any>(
                `${this.apiUrl}/location/monitoring`,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getOrderList(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getOrderListActive(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/aktive-mieten/34hlkj2345lkjfhu76ftz2ui3gf243tbkjlwhg5kjgh2lb23v`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLogs(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/logs`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getRequestLogs(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/request-logs`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    replayRequestLog(id: number) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .get<any>(
                `${this.apiUrl}/private/request-log/${id}/replay`,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getItemList(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/items`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getTextConstants(filter) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/text-constants`,
                {
                    filter
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    updateTextConstant(item) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/text-constant/edit`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLockLogs(type) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/logs/${type}`,
                {},
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLockStatistics(locationId: number) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/statistics/` + locationId,
                {},
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editOrderItem(orderItem) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/edit`,
                {
                    orderItem,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editOrderItemDate(orderItem) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/editDate`,
                {
                    orderItem,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getCompanies() {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/companies`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getDomains(): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/domains`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }


    public getFranchisees(): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/franchisees/`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public editFranchisee(franchisee) {
        return this.http
            .post<any>(
                `${this.apiUrl}/franchisees/`,
                {
                    franchisee,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }


    addFranchisee(franchisee) {
        return this.http
            .post<any>(
                `${this.apiUrl}/franchisees/add`,
                {
                    franchisee,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getStorageLocations(options: any = [], locationId: number = 0): Observable<any[]> {
        let ordering = options.orderBy ? ('?orderBy=' + options.orderBy[0] + '&dir=' + options.orderBy[1]) : '';
        let appendLocationId = locationId > 0 ? '&locationId=' + locationId : ''

        return this.http
            .get<any>(
                `${this.apiUrl}/location/storage-locations` + ordering + appendLocationId,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    editStorageLocation(storageLocation) {
        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-location/${storageLocation.id}`,
                {
                    storageLocation,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }


    addStorageLocation(storageLocation) {
        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-location`,
                {
                    storageLocation,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public sendGetCall(url): Observable<any[]> {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/send-get-call`,
                {
                    url
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    updateDomain(domain) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/domains/edit`,
                {
                    domain
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deleteDomain(domain) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/domains/delete`,
                {
                    domain
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    openLockAsAdmin(item, sendAltSignal: boolean = false, type: string = '') {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/open`,
                {
                    item, sendAltSignal, type
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deletePriceGroupEntry(id) {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/price/delete`,
                {
                    id: id,
                },
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getStatisticsFilter() {
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        console.log('Calling ' + `${this.apiUrl}/private/statistics-filter`)
        return this.http
            .get<any>(
                `${this.apiUrl}/private/statistics-filter`,
                { headers }
            ).pipe(
                map((res: any) => {
                    console.log(846, res)
                    return res;
                })
            )
    }

    getControllingStatistics(filter: any) {
        console.log(853, filter)
        const headers = new HttpHeaders({
            // Authorization: 'Bearer public'
        });

        console.log('Calling ' + `${this.apiUrl}/private/statistics`)
        return this.http
            .post<any>(
                `${this.apiUrl}/private/statistics`,
                {
                    filter: filter
                },
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            )
    }
}
